import React, { useRef, useState, useEffect } from 'react';
import { resumeAudioContext, setGainForElement, getGainValueForElement } from '../utils/audioGraph';
import { Play, Pause, SkipBack, SkipForward, Volume2, Gauge, Plus, Minus, RotateCcw } from 'lucide-react';
import * as Slider from '@radix-ui/react-slider';
import { Chapter } from '../types';
import { useLanguage } from '../contexts/LanguageContext';

// Audio player component with auto-play support
interface AudioPlayerProps {
  chapter: Chapter | null;
  onNextChapter: () => void;
  onPreviousChapter: () => void;
  bookmarkToLoad?: { chapterId: string; timestamp: number } | null;
  onTimeUpdate?: (time: number) => void;
  onBookmarkLoaded?: () => void;
  playbackRate: number;
  onPlaybackRateChange: (rate: number) => void;
  nextChapter?: Chapter | null; // For preloading
  shouldAutoPlay?: boolean; // Auto-play when chapter changes
  onAutoPlayHandled?: () => void; // Callback after auto-play is handled
}

export const AudioPlayer: React.FC<AudioPlayerProps> = ({
  chapter,
  onNextChapter,
  onPreviousChapter,
  bookmarkToLoad,
  onTimeUpdate,
  onBookmarkLoaded,
  playbackRate,
  onPlaybackRateChange,
  nextChapter,
  shouldAutoPlay,
  onAutoPlayHandled,
}) => {
  const { t } = useLanguage();
  const audioRef = useRef<HTMLAudioElement>(null);
  const originalBgVolumesRef = useRef<Record<string, number>>({});
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(() => {
    const saved = localStorage.getItem('audiobookChapterVolume');
    return saved ? parseFloat(saved) : 1;
  });
  const [showSpeedControls, setShowSpeedControls] = useState(false);
  const [preloadedChapter, setPreloadedChapter] = useState<string | null>(null);
  const [audioKey, setAudioKey] = useState(0);
  
  // Detect if we're on mobile
  const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);

  // Create new audio element for each chapter to avoid conflicts
  useEffect(() => {
    if (chapter) {
      console.log('🔄 Loading NEW chapter with dedicated audio:', chapter.title);
      
      // Force new audio element for each chapter
      setAudioKey(prev => prev + 1);
      setIsPlaying(false);
      setCurrentTime(0);
      setDuration(0);
      
      console.log('✅ New dedicated audio element created for:', chapter.title);
    }
  }, [chapter?.id]); // Only trigger on chapter ID change

  // Temporarily reduce background audio volume while chapter plays
  const duckBackground = () => {
    try {
      const ids = ['persistent-audio-player', 'chapter-background-music-player'];
      ids.forEach((id) => {
        const el = document.getElementById(id) as HTMLAudioElement | null;
        if (el && !el.paused) {
          const current = getGainValueForElement(el);
          if (originalBgVolumesRef.current[id] === undefined) {
            originalBgVolumesRef.current[id] = current;
          }
          const target = Math.max(0, Math.min(1, current * 0.6));
          setGainForElement(el, target);
        }
      });
    } catch {}
  };

  // Restore background audio volumes to original values
  const restoreBackground = () => {
    try {
      Object.entries(originalBgVolumesRef.current).forEach(([id, vol]) => {
        const el = document.getElementById(id) as HTMLAudioElement | null;
        if (el) {
          setGainForElement(el, Math.max(0, Math.min(1, vol)));
        }
      });
      originalBgVolumesRef.current = {};
    } catch {}
  };

  // Initialize audio element once it's created - ONLY when audioKey changes (new chapter)
  useEffect(() => {
    if (audioRef.current && chapter) {
      const audio = audioRef.current;
      console.log('🎵 Initializing audio element for:', chapter.title);

      // Set basic properties only
      audio.preload = 'metadata';
      audio.volume = volume;
      audio.playbackRate = playbackRate;

      console.log('✅ Audio initialized:', {
        preload: audio.preload,
        volume: audio.volume,
        playbackRate: audio.playbackRate
      });
    }
  }, [audioKey]); // ONLY audioKey - not volume/playbackRate to prevent recreation

  // Auto-play when chapter changes (user clicked next/previous)
  useEffect(() => {
    if (shouldAutoPlay && audioRef.current && chapter) {
      console.log('🎬 Auto-play triggered for new chapter:', chapter.title);

      const attemptAutoPlay = async () => {
        if (!audioRef.current) return;

        // Wait for audio to be ready
        const waitForReady = () => {
          return new Promise<void>((resolve) => {
            if (audioRef.current && audioRef.current.readyState >= 2) {
              resolve();
            } else {
              const handleCanPlay = () => {
                audioRef.current?.removeEventListener('canplay', handleCanPlay);
                resolve();
              };
              audioRef.current?.addEventListener('canplay', handleCanPlay);

              // Timeout after 3 seconds
              setTimeout(() => {
                audioRef.current?.removeEventListener('canplay', handleCanPlay);
                resolve();
              }, 3000);
            }
          });
        };

        await waitForReady();

        try {
          // Attempt to play chapter and background together
          const playOps: Promise<any>[] = [];
          playOps.push(audioRef.current.play());

          try {
            const bgEl = document.getElementById('chapter-background-music-player') as HTMLAudioElement | null;
            const bgSrc = bgEl?.src;
            if (bgEl && bgSrc && bgEl.paused) {
              playOps.push(bgEl.play());
            }
          } catch {}

          await Promise.allSettled(playOps);
          setIsPlaying(true);
          console.log('🎬 ✅ Auto-play successful (chapter + bg, if any):', chapter.title);
        } catch (error) {
          console.error('🎬 ❌ Auto-play failed:', error);
          // Silently fail - user can manually press play
        }

        // Signal that auto-play was handled
        if (onAutoPlayHandled) {
          onAutoPlayHandled();
        }
      };

      attemptAutoPlay();
    }
  }, [shouldAutoPlay, chapter?.id, onAutoPlayHandled]);

  // Remove the automatic play/pause effect to prevent conflicts
  // Let the user control playback through the button only

  useEffect(() => {
    if (bookmarkToLoad && audioRef.current && chapter && bookmarkToLoad.chapterId === chapter.id) {
      console.log('🔖 Cargando marcador:', bookmarkToLoad);
      
      const loadBookmark = async () => {
        if (audioRef.current) {
          console.log('🔖 Cargando marcador - Estado del audio:', {
            readyState: audioRef.current.readyState,
            paused: audioRef.current.paused,
            currentTime: audioRef.current.currentTime,
            duration: audioRef.current.duration
          });

          // Asegurar que el audio esté listo
          audioRef.current.preload = 'metadata';
          
          // Establecer posición
          audioRef.current.currentTime = bookmarkToLoad.timestamp;
          setCurrentTime(bookmarkToLoad.timestamp);
          console.log('🔖 Posición establecida a:', bookmarkToLoad.timestamp, 'segundos');
          
          // Esperar un momento para que se establezca la posición
          await new Promise(resolve => setTimeout(resolve, 100));
          
          // Auto-reproducir desde el marcador
          console.log('🔖 Intentando iniciar reproducción desde marcador...');
          
          try {
            await audioRef.current.play();
            setIsPlaying(true);
            console.log('🔖 ✅ Reproducción iniciada exitosamente desde marcador');
            
            // Notificar que el marcador fue cargado
            if (onBookmarkLoaded) {
              onBookmarkLoaded();
            }
          } catch (error) {
            console.error('🔖 ❌ Error iniciando reproducción desde marcador:', error);
            // Aún así notificar que el marcador fue cargado (posición establecida)
            if (onBookmarkLoaded) {
              onBookmarkLoaded();
            }
            // Mostrar que el usuario debe hacer click para reproducir
            console.log('🔖 Autoplay bloqueado - usuario debe hacer click manual');
          }
        }
      };

      // Si el audio ya tiene metadata cargada, cargar inmediatamente
      if (audioRef.current.readyState >= 1) {
        loadBookmark();
      } else {
        // Esperar a que se carguen los metadatos
        const handleLoadedMetadata = () => {
          loadBookmark();
          audioRef.current?.removeEventListener('loadedmetadata', handleLoadedMetadata);
        };
        audioRef.current.addEventListener('loadedmetadata', handleLoadedMetadata);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bookmarkToLoad, chapter]);

  useEffect(() => {
    const audio = audioRef.current;
    resumeAudioContext();
    if (!audio) return;

    const handleTimeUpdate = () => {
      const newTime = audio.currentTime;
      const dur = audio.duration;
      setCurrentTime(newTime);
      if (onTimeUpdate) {
        onTimeUpdate(newTime);
      }
      
      // Smart preloading: preload next chapter when 30 seconds remaining
      const remaining = dur - newTime;
      if (remaining <= 30 && nextChapter && preloadedChapter !== nextChapter.id) {
        console.log('🚀 Smart preloading next chapter:', nextChapter.title);
        const preloadAudio = new Audio();
        preloadAudio.preload = 'metadata';
        preloadAudio.src = nextChapter.audioUrl;
        setPreloadedChapter(nextChapter.id);
      }
    };
    
    const handleLoadedMetadata = () => {
      console.log('Audio metadata loaded, duration:', audio.duration);
      setDuration(audio.duration);
      setCurrentTime(audio.currentTime); // Reset current time when metadata loads
    };
    
    const handleEnded = () => {
      console.log('📖 Audiobook chapter ended');
      setIsPlaying(false);
      restoreBackground();
      
      // Opcional: Reanudar música de fondo cuando termina el capítulo
      // const backgroundAudio = document.getElementById('persistent-audio-player') as HTMLAudioElement;
      // if (backgroundAudio && backgroundAudio.paused) {
      //   console.log('▶️ Resuming background music after chapter end');
      //   backgroundAudio.play().catch(console.error);
      // }
      
      onNextChapter();
    };

    const handleCanPlayThrough = () => {
      console.log('Audio can play through');
    };

    const handleError = (e: Event) => {
      console.error('Audio error:', e);
      setIsPlaying(false);
    };

    const handleLoadStart = () => {
      console.log('Audio load started');
    };

    audio.addEventListener('timeupdate', handleTimeUpdate);
    audio.addEventListener('loadedmetadata', handleLoadedMetadata);
    audio.addEventListener('ended', handleEnded);
    audio.addEventListener('canplaythrough', handleCanPlayThrough);
    audio.addEventListener('error', handleError);
    audio.addEventListener('loadstart', handleLoadStart);

    return () => {
      audio.removeEventListener('timeupdate', handleTimeUpdate);
      audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
      audio.removeEventListener('ended', handleEnded);
      audio.removeEventListener('canplaythrough', handleCanPlayThrough);
      audio.removeEventListener('error', handleError);
      audio.removeEventListener('loadstart', handleLoadStart);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onNextChapter, audioKey, nextChapter, preloadedChapter, onTimeUpdate]);

  // Control de velocidad de reproducción - separated to avoid recreation
  useEffect(() => {
    if (audioRef.current) {
      console.log('🎛️ Setting playback rate:', playbackRate);
      audioRef.current.playbackRate = playbackRate;
    }
  }, [playbackRate]);

  // Control de volumen con WebAudio (iOS compatible)
  useEffect(() => {
    if (audioRef.current) {
      console.log('🔊 Setting chapter gain:', volume);
      setGainForElement(audioRef.current, volume);
    }
  }, [volume]);

  // Persist chapter volume independently
  useEffect(() => {
    try {
      localStorage.setItem('audiobookChapterVolume', String(volume));
    } catch {}
  }, [volume]);

  // Configure Media Session API for background playback
  useEffect(() => {
    if ('mediaSession' in navigator && chapter) {
      // Set metadata for lock screen display
      navigator.mediaSession.metadata = new MediaMetadata({
        title: chapter.title,
        artist: 'Dante Zetta',
        album: '11000',
        artwork: [
          { src: '/book.svg', sizes: '192x192', type: 'image/svg+xml' },
          { src: '/book.svg', sizes: '512x512', type: 'image/svg+xml' },
          { src: '/book.svg', sizes: '64x64', type: 'image/svg+xml' }
        ]
      });

      // Update playback state
      navigator.mediaSession.playbackState = isPlaying ? 'playing' : 'paused';

      // Play/Pause handlers
      navigator.mediaSession.setActionHandler('play', async () => {
        console.log('🎵 Media Session: Play');
        if (audioRef.current && !isPlaying) {
          try {
            await audioRef.current.play();
            setIsPlaying(true);
          } catch (err) {
            console.error('Error playing audio:', err);
          }
        }
      });

      navigator.mediaSession.setActionHandler('pause', () => {
        console.log('⏸️ Media Session: Pause');
        if (audioRef.current && isPlaying) {
          audioRef.current.pause();
          setIsPlaying(false);
        }
      });

      // Track navigation
      navigator.mediaSession.setActionHandler('previoustrack', () => {
        console.log('⏮️ Media Session: Previous');
        onPreviousChapter();
      });

      navigator.mediaSession.setActionHandler('nexttrack', () => {
        console.log('⏭️ Media Session: Next');
        onNextChapter();
      });

      // Seek controls (10 seconds)
      navigator.mediaSession.setActionHandler('seekbackward', (details) => {
        if (audioRef.current) {
          const skipTime = details?.seekOffset || 10;
          audioRef.current.currentTime = Math.max(0, audioRef.current.currentTime - skipTime);
          console.log(`⏪ Skipped back ${skipTime}s`);
        }
      });

      navigator.mediaSession.setActionHandler('seekforward', (details) => {
        if (audioRef.current) {
          const skipTime = details?.seekOffset || 10;
          audioRef.current.currentTime = Math.min(duration, audioRef.current.currentTime + skipTime);
          console.log(`⏩ Skipped forward ${skipTime}s`);
        }
      });

      // Seek to specific position
      navigator.mediaSession.setActionHandler('seekto', (details) => {
        if (audioRef.current && details?.seekTime !== undefined) {
          audioRef.current.currentTime = details.seekTime;
          console.log(`⏺️ Seeked to ${details.seekTime}s`);
        }
      });

      // Update position state for progress bar on lock screen
      if ('setPositionState' in navigator.mediaSession) {
        try {
          navigator.mediaSession.setPositionState({
            duration: duration || 0,
            playbackRate: playbackRate,
            position: currentTime || 0
          });
        } catch (err) {
          // Ignore errors from invalid states
        }
      }
    }
  }, [chapter, isPlaying, onNextChapter, onPreviousChapter, duration, currentTime, playbackRate]);

  // Configure audio element for background playback - MUST be tied to audioKey
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) {
      console.log('❌ No audio ref for event listeners');
      return;
    }

    console.log('🔗 Setting up event listeners for audio element');

    // Set audio attributes for background playbook
    audio.setAttribute('playsinline', 'true');
    audio.setAttribute('webkit-playsinline', 'true');
    audio.setAttribute('x-webkit-airplay', 'allow');
    
    // Simple event handlers for state sync
    const handlePlay = () => {
      console.log('🎧 Audio play event - SHOULD BE PLAYING NOW → Button should show PAUSE icon');
      setIsPlaying(true);
      duckBackground();
    };
    
    const handlePause = () => {
      console.log('⏸️ Audio pause event - AUDIO WAS PAUSED → Button should show PLAY icon');
      setIsPlaying(false);
      restoreBackground();
    };
    
    const handleLoadStart = () => {
      console.log('📥 Audio load started');
    };
    
    const handleCanPlay = () => {
      console.log('✅ Audio can play now');
    };
    
    const handleError = (e: any) => {
      console.error('❌ AUDIO ERROR:', e);
    };

    console.log('📎 Adding event listeners to audio element');
    audio.addEventListener('play', handlePlay);
    audio.addEventListener('pause', handlePause);
    audio.addEventListener('loadstart', handleLoadStart);
    audio.addEventListener('canplay', handleCanPlay);
    audio.addEventListener('error', handleError);

    return () => {
      console.log('🗑️ Removing event listeners from audio element');
      audio.removeEventListener('play', handlePlay);
      audio.removeEventListener('pause', handlePause);
      audio.removeEventListener('loadstart', handleLoadStart);
      audio.removeEventListener('canplay', handleCanPlay);
      audio.removeEventListener('error', handleError);
    };
  }, [audioKey]); // Tied to audioKey so listeners are added to new elements

  const togglePlayPause = async () => {
    if (!audioRef.current || !chapter) {
      console.log('❌ NO AUDIO REF OR CHAPTER:', { hasAudio: !!audioRef.current, hasChapter: !!chapter });
      return;
    }
    
    const audio = audioRef.current;
    
    console.log('🎮 TOGGLE PLAY/PAUSE:', {
      paused: audio.paused,
      src: audio.src,
      readyState: audio.readyState,
      duration: audio.duration,
      currentTime: audio.currentTime,
      networkState: audio.networkState
    });
    
    try {
      if (audio.paused) {
        console.log('▶️ ATTEMPTING TO PLAY...');
        
        // Pause ONLY other audiobook chapters, keep background music
        const allAudio = document.querySelectorAll('audio');
        console.log('🔍 Found', allAudio.length, 'total audio elements');
        
        allAudio.forEach((otherAudio, index) => {
          console.log(`Audio ${index}:`, {
            id: otherAudio.id,
            src: otherAudio.src?.substring(0, 50) + '...',
            paused: otherAudio.paused
          });
          
          if (otherAudio !== audio && 
              otherAudio.id !== 'persistent-audio-player' &&
              otherAudio.id !== 'chapter-background-music-player' &&
              !otherAudio.src?.includes('Ilusion_') &&
              !otherAudio.paused) {
            console.log('⏸️ Pausing other audiobook:', index);
            otherAudio.pause();
          }
        });
        
        console.log('🎵 About to play chapter and (if available) background...');
        const playPromises: Promise<any>[] = [];
        playPromises.push(audio.play());

        try {
          const bgEl = document.getElementById('chapter-background-music-player') as HTMLAudioElement | null;
          const bgSrc = bgEl?.src;
          if (bgEl && bgSrc) {
            if (bgEl.paused) {
              playPromises.push(bgEl.play());
            }
          }
        } catch (e) {
          console.log('⚠️ Could not queue background music play:', e);
        }

        await Promise.allSettled(playPromises);
        console.log('✅ play() calls settled (chapter + background)');
        
      } else {
        console.log('⏸️ ATTEMPTING TO PAUSE...');
        audio.pause();
        console.log('✅ PAUSE completed');
      }
    } catch (err) {
      console.error('❌ PLAY/PAUSE ERROR:', err);
      console.error('Error details:', {
        name: err instanceof Error ? err.name : 'Unknown',
        message: err instanceof Error ? err.message : err
      });
      
      if (err instanceof Error && err.name === 'NotAllowedError') {
        alert('El navegador bloquea la reproducción automática. Presiona el botón para reproducir.');
      }
    }
  };

  const handleSeek = (value: number[]) => {
    if (audioRef.current) {
      const wasPlaying = !audioRef.current.paused;
      console.log('🎯 Seeking to:', value[0], 'Was playing:', wasPlaying, 'ReadyState:', audioRef.current.readyState);
      
      // Store the playing state before seek
      const shouldResume = wasPlaying;
      
      // Pause if playing to prevent issues during seek
      if (wasPlaying) {
        audioRef.current.pause();
      }
      
      // Set the new position
      audioRef.current.currentTime = value[0];
      setCurrentTime(value[0]);
      
      // Resume if was playing
      if (shouldResume) {
        // Small delay to allow audio to process the seek
        setTimeout(() => {
          if (audioRef.current) {
            console.log('🔄 Resuming playback after seek');
            audioRef.current.play().catch((err) => {
              console.error('Error resuming after seek:', err);
              // Force sync state
              setIsPlaying(false);
            });
          }
        }, 100);
      }
    }
  };

  const handleVolumeChange = (value: number[]) => {
    if (audioRef.current) {
      audioRef.current.volume = value[0];
      setVolume(value[0]);
    }
  };

  // Volume control functions for desktop buttons
  const handleVolumeIncrease = () => {
    const newVolume = Math.min(1, volume + 0.1);
    if (audioRef.current) {
      audioRef.current.volume = newVolume;
      setVolume(newVolume);
    }
  };

  const handleVolumeDecrease = () => {
    const newVolume = Math.max(0, volume - 0.1);
    if (audioRef.current) {
      audioRef.current.volume = newVolume;
      setVolume(newVolume);
    }
  };

  const handleVolumeMute = () => {
    const newVolume = volume > 0 ? 0 : 1;
    if (audioRef.current) {
      audioRef.current.volume = newVolume;
      setVolume(newVolume);
    }
  };

  const handleSpeedIncrease = () => {
    const newRate = Math.min(playbackRate + 0.1, 1.2);
    onPlaybackRateChange(Math.round(newRate * 10) / 10);
  };

  const handleSpeedDecrease = () => {
    const newRate = Math.max(playbackRate - 0.1, 0.9);
    onPlaybackRateChange(Math.round(newRate * 10) / 10);
  };

  const toggleSpeedControls = () => {
    setShowSpeedControls(!showSpeedControls);
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleReloadPage = () => {
    // Clear cache and reload with timestamp
    const timestamp = new Date().getTime();
    window.location.href = `${window.location.pathname}?refresh=${timestamp}`;
  };


  return (
    <div className="audio-player">
      <audio 
        key={`audio-${chapter?.id}-${audioKey}`}
        ref={audioRef}
        preload="metadata"
        controls={false}
        style={{ display: 'none' }}
        playsInline
        webkit-playsinline="true"
        crossOrigin="anonymous"
        src={chapter?.audioUrl ?? undefined}
      />
      
      <div className="player-header">
        <div className="player-info">
          <h3>{chapter?.title || t.noChapterSelected}</h3>
        </div>
        <button 
          className="player-reload-btn" 
          onClick={handleReloadPage}
          title="Recargar aplicación"
        >
          <RotateCcw size={16} />
        </button>
      </div>

      <div className="progress-container">
        <span className="time">{formatTime(currentTime)}</span>
        <Slider.Root
          className="slider-root progress-slider"
          value={[currentTime]}
          max={duration || 100}
          step={1}
          onValueChange={handleSeek}
          disabled={!chapter}
        >
          <Slider.Track className="slider-track">
            <Slider.Range className="slider-range" />
          </Slider.Track>
          <Slider.Thumb className="slider-thumb" />
        </Slider.Root>
        <span className="time">{formatTime(duration)}</span>
      </div>

      <div className="controls">
        <button 
          className="control-btn" 
          onClick={onPreviousChapter}
          disabled={!chapter}
        >
          <SkipBack size={24} />
        </button>
        
        <button 
          className="control-btn play-btn" 
          onClick={togglePlayPause}
          disabled={!chapter}
          title={isPlaying ? 'Pausar' : 'Reproducir'}
        >
          {isPlaying ? (
            <Pause size={32} />
          ) : (
            <Play size={32} />
          )}
        </button>
        
        <button 
          className="control-btn" 
          onClick={onNextChapter}
          disabled={!chapter}
        >
          <SkipForward size={24} />
        </button>
      </div>

      <div className="volume-container">
        {isMobile ? (
          // MOBILE: Slider with better step for touch control
          <>
            <Volume2 size={20} className="volume-icon-left" />
            <Slider.Root
              className="slider-root volume-slider"
              value={[volume]}
              max={1}
              step={0.1}
              onValueChange={handleVolumeChange}
            >
              <Slider.Track className="slider-track">
                <Slider.Range className="slider-range" />
              </Slider.Track>
              <Slider.Thumb className="slider-thumb" />
            </Slider.Root>
            <button
              className="gauge-btn gauge-btn-right"
              onClick={toggleSpeedControls}
              title={t.changeSpeed}
            >
              <Gauge size={18} />
            </button>
          </>
        ) : (
          // DESKTOP: Botones que funcionan mejor
          <>
            <button
              className="volume-btn mute-btn"
              onClick={handleVolumeMute}
              title={volume > 0 ? 'Silenciar' : 'Activar sonido'}
            >
              <Volume2 size={20} />
            </button>

            <button
              className="volume-btn decrease-btn"
              onClick={handleVolumeDecrease}
              disabled={volume <= 0}
              title="Bajar volumen"
            >
              <Minus size={16} />
            </button>

            <span className="volume-percentage-desktop">{Math.round(volume * 100)}%</span>

            <button
              className="volume-btn increase-btn"
              onClick={handleVolumeIncrease}
              disabled={volume >= 1}
              title="Subir volumen"
            >
              <Plus size={16} />
            </button>

            <button
              className="gauge-btn gauge-btn-right"
              onClick={toggleSpeedControls}
              title={t.changeSpeed}
            >
              <Gauge size={18} />
            </button>
          </>
        )}
      </div>

      {showSpeedControls && (
        <div className="speed-controls-container" style={{
          position: 'fixed',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          background: 'white',
          padding: '20px',
          borderRadius: '10px',
          boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
          zIndex: 1000,
          border: '2px solid #ff6b35'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
            <button 
              className="speed-btn decrease" 
              onClick={handleSpeedDecrease}
              disabled={playbackRate <= 0.9}
              title={t.reduceSpeed}
            >
              <Minus size={16} />
            </button>
            
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#ff6b35' }}>
                {playbackRate.toFixed(1)}x
              </div>
              <div style={{ fontSize: '12px', color: '#666' }}>
                {t.speed}
              </div>
            </div>
            
            <button 
              className="speed-btn increase" 
              onClick={handleSpeedIncrease}
              disabled={playbackRate >= 1.2}
              title={t.increaseSpeed}
            >
              <Plus size={16} />
            </button>
          </div>
          
          <button 
            onClick={() => setShowSpeedControls(false)}
            style={{
              position: 'absolute',
              top: '5px',
              right: '8px',
              background: 'none',
              border: 'none',
              fontSize: '16px',
              cursor: 'pointer',
              color: '#999'
            }}
          >
            ✕
          </button>
        </div>
      )}

    </div>
  );
};
